<script lang="ts" setup>
import MembershipLevel, { Level } from './Level.vue';
import { ref } from 'vue';
import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Swiper as SwiperClass, SwiperModule } from 'swiper/types';
import { Icon } from '@iconify/vue';

export interface Group {
  title: string;
  levels: Level[];
}

interface Props {
  group: Group;
}

defineProps<Props>();

const modules: SwiperModule[] = [A11y];
const swiperUse = ref<SwiperClass | null>(null);

function onSwiper(swiper: SwiperClass) {
  swiperUse.value = swiper;
}

const swiperOptions = {
  init: false,
  wrapperTag: 'div',
  modules,
  slidesPerView: 'auto',
  keyboard: {
    enabled: true,
    onlyInViewport: false,
  },
};
</script>

<template>
  <div v-if="group.levels && group.levels.length > 0" class="membership-group">
    <div class="membership-group__navigation">
      <button
        class="swiper-button-prev"
        :class="{
          'swiper-button-disabled': swiperUse?.isBeginning,
          'swiper-button-lock': swiperUse?.isLocked,
        }"
        @click="swiperUse?.slidePrev()"
      >
        <span class="sr-only">Prev Slide</span>
        <Icon icon="bi:arrow-left" />
      </button>
      <button
        class="swiper-button-next"
        :class="{
          'swiper-button-disabled': swiperUse?.isEnd,
          'swiper-button-lock': swiperUse?.isLocked,
        }"
        @click="swiperUse?.slideNext()"
      >
        <span class="sr-only">Next Slide</span>
        <Icon icon="bi:arrow-right" />
      </button>
    </div>
    <Swiper v-bind="swiperOptions" @swiper="onSwiper">
      <SwiperSlide v-for="(level, index) in group.levels" :key="`membership-group-item-${index}`">
        <MembershipLevel :level="level" />
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style lang="scss" scoped>
.membership-group {
  &__navigation {
    @apply flex items-center justify-center;
    @apply xl:justify-end;
  }

  :deep(.swiper) {
    @apply w-[calc(100%+1.5rem)] -ml-3 overflow-visible;
  }

  :deep(.swiper-wrapper) {
    @apply flex items-stretch;
  }

  :deep(.swiper-slide) {
    @apply w-full px-3 h-auto;
    @apply md:w-1/2;
    @apply lg:w-1/3;
    @apply 2xl:w-1/4;
  }

  .swiper {
    @apply w-[calc(100%+1.5rem)] overflow-visible;

    &-button-prev,
    &-button-next {
      @apply static text-accent after:hidden translate-y-0 mb-6 mt-0 mx-1 h-auto w-auto p-2 [&>svg]:w-10 duration-300 hover:text-dark;
      @apply xl:-mt-[98px];
    }
  }
}
</style>
